// ABOUTME: Switches a jf-tabs block's panels: clicking a tab shows its panel and hides the others; the arrow keys, Home and
// End move between tabs as the WAI-ARIA tabs pattern describes. Marks the element ready so its no-script first-panel rule steps aside.
class JfTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    const tabs = this.#tabs();
    const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    if (selected) this.#select(selected, false);
    this.dataset.ready = '';
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @returns {HTMLElement[]} */
  #tabs() {
    return [...this.querySelectorAll(':scope > .jf-tabs__list > [role="tab"]')];
  }

  /**
   * @param {HTMLElement} tab
   * @param {boolean} focus
   */
  #select(tab, focus) {
    for (const other of this.#tabs()) {
      const on = other === tab;
      other.setAttribute('aria-selected', on ? 'true' : 'false');
      other.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(other.getAttribute('aria-controls') || '')}`);
      if (panel instanceof HTMLElement) panel.hidden = !on;
    }
    if (focus) tab.focus();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = /** @type {Element} */ (event.target).closest('[role="tab"]');
    if (tab instanceof HTMLElement && this.#tabs().includes(tab)) this.#select(tab, false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.#tabs();
    const current = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (current === -1) return;
    const moves = { ArrowRight: current + 1, ArrowDown: current + 1, ArrowLeft: current - 1, ArrowUp: current - 1, Home: 0, End: tabs.length - 1 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    const next = (moves[/** @type {keyof typeof moves} */ (event.key)] + tabs.length) % tabs.length;
    this.#select(tabs[next], true);
  };
}

if (!customElements.get('jf-tabs')) {
  customElements.define('jf-tabs', JfTabs);
}
